import { useLazyQuery, useMutation } from "@apollo/client";
import { CloudFramePrimaryButton, CloudFrameDefaultButton } from "@cloudframe/button";
import { CloudFrameDatepicker } from "@cloudframe/datepicker";
import { CloudFrameDropdown } from "@cloudframe/dropdown";
import { CloudFrameTextBox } from "@cloudframe/textbox";
import { DayOfWeek, IDropdownOption, Toggle, defaultDatePickerStrings } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { FormEvent, useEffect, useState } from "react";
import {
  CREATE_CONTINUUM_LICENSE,
  CREATE_SCANNER_LICENSE,
} from "../../graphql-mutations/continuum-scanner-license.mutation";
import {
  QUERY_GET_CONTINUUM_LICENSE,
  QUERY_GET_CONTINUUM_LICENSE_TEXT,
  QUERY_GET_SCANNER_LICENSE_TEXT,
} from "../../graphql-query/continuum-scanner-license.query";
import { saveStringAsTxt } from "../../utils/file.utils";
import { useStyles } from "./continuum-scanner-license-form.styles";

const EMAIL_DOMAIN = "@cloudframe.com";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_CONTINUUM_LOC = "1000000";
const SCANNER_LICENSE_DURATION_DAYS = 60;

type ProductType = "Continuum" | "Scanner" | "CodeNavigator";

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// The earliest an expiry date may be set to: today (no past dates).
const getMinExpiryDate = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

// The earliest a Platform Expiry Date may be set to, given a chosen effective date:
// whichever is later of "today" and "the day after the effective date".
const getMinPlatformExpiryDate = (effectiveDate: Date | undefined): Date => {
  const minExpiry = getMinExpiryDate();
  if (!effectiveDate) return minExpiry;
  const dayAfterEffective = addDays(new Date(effectiveDate), 1);
  return dayAfterEffective > minExpiry ? dayAfterEffective : minExpiry;
};

const PRODUCT_OPTIONS: IDropdownOption[] = [
  { key: "Continuum", text: "Continuum" },
  { key: "Scanner", text: "Scanner" },
  { key: "CodeNavigator", text: "CodeNavigator" },
];

const CONTINUUM_LICENSE_TYPE_OPTIONS: IDropdownOption[] = [
  { key: "Internal", text: "Internal" },
  { key: "Commercial", text: "Commercial" },
  { key: "POC_Pilot", text: "POC/Pilot" },
];

const LICENSE_CATEGORY_OPTIONS: IDropdownOption[] = [
  { key: "Platform", text: "Platform" },
  { key: "Production", text: "Production" },
];

const ContinuumScannerLicenseForm = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductType>("Continuum");
  const [formValues, setFormValues] = useState<any>({
    linesOfCode: DEFAULT_CONTINUUM_LOC,
    modernizationAllowed: "false",
    isIncremental: "false",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [createContinuumLicense] = useMutation(CREATE_CONTINUUM_LICENSE);
  const [createScannerLicense] = useMutation(CREATE_SCANNER_LICENSE);
  const [loadContinuumLicenseText] = useLazyQuery(QUERY_GET_CONTINUUM_LICENSE_TEXT);
  const [loadScannerLicenseText] = useLazyQuery(QUERY_GET_SCANNER_LICENSE_TEXT);
  const [createdLicense, setCreatedLicense] = useState<{
    licenseId: string;
    product: ProductType;
  } | null>(null);

  const [previousLicenseLookup, setPreviousLicenseLookup] = useState<{
    status: "idle" | "loading" | "found" | "not-found" | "not-activated";
    carryOverLOC: boolean;
  }>({ status: "idle", carryOverLOC: false });
  const [loadPreviousContinuumLicense] = useLazyQuery(QUERY_GET_CONTINUUM_LICENSE, {
    fetchPolicy: "network-only",
  });

  const updateField = (field: string, value: any) => {
    setFormValues((prev: any) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const isContinuumFlow = product !== "Scanner";
  const isPlatform = formValues.licenseCategory === "Platform";
  const isProductionBlocked =
    isContinuumFlow && formValues.licenseCategory === "Production";
  const isIncremental = formValues.isIncremental === "true";
  const isCarryOverUnresolved =
    isPlatform &&
    isIncremental &&
    !!formValues.previousLicenseId &&
    previousLicenseLookup.status !== "found";

  useEffect(() => {
    if (!isIncremental || !formValues.previousLicenseId) {
      setPreviousLicenseLookup({ status: "idle", carryOverLOC: false });
      return;
    }
    const licenseId = formValues.previousLicenseId;
    setPreviousLicenseLookup({ status: "loading", carryOverLOC: false });
    const timer = setTimeout(async () => {
      try {
        const { data } = await loadPreviousContinuumLicense({
          variables: { licenseId },
        });
        const previousLicense = data?.getContinuumLicense;
        if (!previousLicense) {
          setPreviousLicenseLookup({ status: "not-found", carryOverLOC: false });
          return;
        }
        if (previousLicense.licenseStatus === "PENDING") {
          setPreviousLicenseLookup({ status: "not-activated", carryOverLOC: false });
          return;
        }
        const isExpired =
          !!previousLicense.platformExpiryDate &&
          new Date(previousLicense.platformExpiryDate) < new Date();
        setPreviousLicenseLookup({ status: "found", carryOverLOC: !isExpired });
      } catch {
        setPreviousLicenseLookup({ status: "not-found", carryOverLOC: false });
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncremental, formValues.previousLicenseId]);

  const REQUIRED_FIELD_LABEL = "Please fill out this field";

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formValues.email) {
      errors.email = REQUIRED_FIELD_LABEL;
    } else if (!EMAIL_PATTERN.test(formValues.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formValues.customerName) errors.customerName = REQUIRED_FIELD_LABEL;
    if (!formValues.jiraTicket) errors.jiraTicket = REQUIRED_FIELD_LABEL;
    if (!formValues.serviceDeskID) errors.serviceDeskID = REQUIRED_FIELD_LABEL;

    if (isContinuumFlow) {
      if (!formValues.licenseType) errors.licenseType = REQUIRED_FIELD_LABEL;
      if (!formValues.licenseCategory) errors.licenseCategory = REQUIRED_FIELD_LABEL;
      if (isPlatform) {
        if (!formValues.linesOfCode) errors.linesOfCode = REQUIRED_FIELD_LABEL;
        if (!formValues.platformEffectiveDate)
          errors.platformEffectiveDate = REQUIRED_FIELD_LABEL;
        if (!formValues.platformExpiryDate) {
          errors.platformExpiryDate = REQUIRED_FIELD_LABEL;
        } else if (
          new Date(formValues.platformExpiryDate) < getMinExpiryDate()
        ) {
          errors.platformExpiryDate =
            "Expiry date cannot be in the past.";
        } else if (
          formValues.platformEffectiveDate &&
          new Date(formValues.platformExpiryDate) <=
            new Date(formValues.platformEffectiveDate)
        ) {
          errors.platformExpiryDate =
            "Expiry date must be after the effective date.";
        }
        if (!formValues.modernizationAllowed)
          errors.modernizationAllowed = REQUIRED_FIELD_LABEL;
        if (!formValues.isIncremental) errors.isIncremental = REQUIRED_FIELD_LABEL;
        if (formValues.isIncremental === "true" && !formValues.previousLicenseId)
          errors.previousLicenseId = REQUIRED_FIELD_LABEL;
      }
    } else {
      if (!formValues.scannerEffectiveDate)
        errors.scannerEffectiveDate = REQUIRED_FIELD_LABEL;
      if (!formValues.scannerExpiryDate) {
        errors.scannerExpiryDate = REQUIRED_FIELD_LABEL;
      } else if (new Date(formValues.scannerExpiryDate) < getMinExpiryDate()) {
        errors.scannerExpiryDate =
          "Expiry date must be at least one day from today.";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isProductionBlocked) {
      return;
    }
    if (!validate()) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isContinuumFlow) {
        const result = await createContinuumLicense({
          variables: {
            data: {
              product,
              email: formValues.email,
              customerName: formValues.customerName,
              jiraTicket: formValues.jiraTicket,
              serviceDeskID: formValues.serviceDeskID,
              department: formValues.department,
              licenseType: formValues.licenseType,
              licenseCategory: formValues.licenseCategory,
              platformEffectiveDate: formValues.platformEffectiveDate
                ? (formValues.platformEffectiveDate as Date).toISOString()
                : undefined,
              platformExpiryDate: formValues.platformExpiryDate
                ? (formValues.platformExpiryDate as Date).toISOString()
                : undefined,
              modernizationAllowed: isPlatform
                ? formValues.modernizationAllowed === "true"
                : undefined,
              linesOfCode: isPlatform ? Number(formValues.linesOfCode) : undefined,
              isIncremental: isPlatform
                ? formValues.isIncremental === "true"
                : undefined,
              previousLicenseId:
                isPlatform && formValues.isIncremental === "true"
                  ? formValues.previousLicenseId
                  : undefined,
            },
          },
        });
        setCreatedLicense({
          licenseId: result.data.createContinuumLicense.licenseId,
          product,
        });
      } else {
        const result = await createScannerLicense({
          variables: {
            data: {
              email: formValues.email,
              customerName: formValues.customerName,
              jiraTicket: formValues.jiraTicket,
              serviceDeskID: formValues.serviceDeskID,
              department: formValues.department,
              licenseType: "Assessment",
              scannerEffectiveDate: formValues.scannerEffectiveDate
                ? (formValues.scannerEffectiveDate as Date).toISOString()
                : undefined,
              scannerExpiryDate: formValues.scannerExpiryDate
                ? (formValues.scannerExpiryDate as Date).toISOString()
                : undefined,
            },
          },
        });
        setCreatedLicense({
          licenseId: result.data.createScannerLicense.licenseId,
          product: "Scanner",
        });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadCreatedLicense = async () => {
    if (!createdLicense) return;
    const loadText =
      createdLicense.product !== "Scanner"
        ? loadContinuumLicenseText
        : loadScannerLicenseText;
    const { data } = await loadText({
      variables: { licenseId: createdLicense.licenseId },
    });
    const licenseText =
      data?.getContinuumLicenseText?.licenseText ||
      data?.getScannerLicenseText?.licenseText;
    if (licenseText) {
      saveStringAsTxt(licenseText, "Cloudframe_License");
    }
  };

  const handleGoToList = () => {
    navigate("/all-continuum-scanner-licenses", {
      state: { highlightLicenseId: createdLicense?.licenseId },
    });
  };

  if (createdLicense) {
    return (
      <div className={classes.root}>
        <div className={classes.formBody}>
          <div className={classes.subContainer}>
            <h2>License created</h2>
            <div className={classes.successBanner}>
              License <strong>{createdLicense.licenseId}</strong> was created successfully.
            </div>
            <div className={classes.btnContainer}>
              <CloudFramePrimaryButton
                text="Download license"
                onClick={handleDownloadCreatedLicense}
              />
              <CloudFrameDefaultButton
                text="Go to all licenses"
                onClick={handleGoToList}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div id="form" className={classes.formBody}>
        <div className={classes.subContainer}>
          <h2>Create Continuum / Scanner / CodeNavigator License</h2>
          <form onSubmit={handleSubmit} noValidate>
            <CloudFrameDropdown
              label="Product"
              className={classes.mb8}
              selectedKey={product}
              options={PRODUCT_OPTIONS}
              onChange={(_, option) => {
                const nextProduct = option?.key as ProductType;
                setProduct(nextProduct);
                setFormValues(
                  nextProduct !== "Scanner"
                    ? {
                        linesOfCode: DEFAULT_CONTINUUM_LOC,
                        modernizationAllowed: "false",
                        isIncremental: "false",
                      }
                    : {}
                );
                setFieldErrors({});
                setError(null);
              }}
            />

            <h3 className={classes.sectionTitle}>Customer Details</h3>
            <div className={classes.fieldRow}>
              <CloudFrameTextBox
                label="Email"
                required
                placeholder={`name${EMAIL_DOMAIN}`}
                className={`${classes.mb8} ${classes.fieldRowItem}`}
                value={formValues.email || ""}
                onChange={(_, v) => updateField("email", v)}
                errorMessage={fieldErrors.email}
              />
              <CloudFrameTextBox
                label="Customer Name"
                required
                className={`${classes.mb8} ${classes.fieldRowItem}`}
                value={formValues.customerName || ""}
                onChange={(_, v) => updateField("customerName", v)}
                errorMessage={fieldErrors.customerName}
              />
            </div>
            <CloudFrameTextBox
              label="Department"
              className={classes.mb8}
              value={formValues.department || ""}
              onChange={(_, v) => updateField("department", v)}
              errorMessage={fieldErrors.department}
            />

            <h3 className={classes.sectionTitle}>Ticket Details</h3>
            <div className={classes.fieldRow}>
              <CloudFrameTextBox
                label="Jira Ticket"
                required
                className={`${classes.mb8} ${classes.fieldRowItem}`}
                value={formValues.jiraTicket || ""}
                onChange={(_, v) => updateField("jiraTicket", v)}
                errorMessage={fieldErrors.jiraTicket}
              />
              <CloudFrameTextBox
                label="ServicedeskID"
                required
                className={`${classes.mb8} ${classes.fieldRowItem}`}
                value={formValues.serviceDeskID || ""}
                onChange={(_, v) => updateField("serviceDeskID", v)}
                errorMessage={fieldErrors.serviceDeskID}
              />
            </div>

            <h3 className={classes.sectionTitle}>License Details</h3>

            {isContinuumFlow && (
              <>
                <div className={classes.fieldRow}>
                  <CloudFrameDropdown
                    label="License Type"
                    required
                    className={classes.fieldRowItem}
                    selectedKey={formValues.licenseType}
                    options={CONTINUUM_LICENSE_TYPE_OPTIONS}
                    onChange={(_, option) => updateField("licenseType", option?.key)}
                    errorMessage={fieldErrors.licenseType}
                  />
                  <CloudFrameDropdown
                    label="License Category"
                    required
                    className={classes.fieldRowItem}
                    selectedKey={formValues.licenseCategory}
                    options={LICENSE_CATEGORY_OPTIONS}
                    onChange={(_, option) =>
                      updateField("licenseCategory", option?.key)
                    }
                    errorMessage={fieldErrors.licenseCategory}
                  />
                </div>

                {isProductionBlocked && (
                  <div className={classes.notice}>
                    Production license category is not yet supported.
                  </div>
                )}

                {isPlatform && (
                  <>
                    <div className={`${classes.fieldRow} ${classes.fieldRowSpaced}`}>
                      <div className={classes.fieldRowItem}>
                        <CloudFrameDatepicker
                          label="Platform Effective Date *"
                          className={classes.mb8}
                          firstDayOfWeek={DayOfWeek.Monday}
                          strings={defaultDatePickerStrings}
                          value={formValues.platformEffectiveDate}
                          onSelectDate={(d) => {
                            const nextEffectiveDate = d ?? undefined;
                            setFormValues((prev: any) => {
                              const expiryStillValid =
                                prev.platformExpiryDate &&
                                nextEffectiveDate &&
                                new Date(prev.platformExpiryDate) >
                                  new Date(nextEffectiveDate);
                              return {
                                ...prev,
                                platformEffectiveDate: nextEffectiveDate,
                                platformExpiryDate: expiryStillValid
                                  ? prev.platformExpiryDate
                                  : undefined,
                              };
                            });
                            setFieldErrors((prev) => {
                              if (!prev.platformEffectiveDate && !prev.platformExpiryDate)
                                return prev;
                              const next = { ...prev };
                              delete next.platformEffectiveDate;
                              delete next.platformExpiryDate;
                              return next;
                            });
                          }}
                        />
                        {fieldErrors.platformEffectiveDate && (
                          <div className={classes.fieldErrorText}>
                            {fieldErrors.platformEffectiveDate}
                          </div>
                        )}
                      </div>
                      <div className={classes.fieldRowItem}>
                        <CloudFrameDatepicker
                          label="Platform Expiry Date *"
                          className={classes.mb8}
                          firstDayOfWeek={DayOfWeek.Monday}
                          strings={defaultDatePickerStrings}
                          value={formValues.platformExpiryDate}
                          minDate={getMinPlatformExpiryDate(
                            formValues.platformEffectiveDate
                          )}
                          onSelectDate={(d) =>
                            updateField("platformExpiryDate", d ?? undefined)
                          }
                        />
                        {fieldErrors.platformExpiryDate && (
                          <div className={classes.fieldErrorText}>
                            {fieldErrors.platformExpiryDate}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={classes.fieldRow}>
                      <CloudFrameTextBox
                        label="Lines of Code"
                        type="number"
                        required
                        className={`${classes.mb8} ${classes.fieldRowItem}`}
                        value={formValues.linesOfCode || ""}
                        onChange={(_, v) => updateField("linesOfCode", v)}
                        errorMessage={fieldErrors.linesOfCode}
                      />
                      <div className={classes.fieldRowItem} />
                    </div>

                    <div className={`${classes.fieldRow} ${classes.toggleRow}`}>
                      <div className={classes.fieldRowItem}>
                        <Toggle
                          label="Modernization Allowed *"
                          onText="Yes"
                          offText="No"
                          checked={formValues.modernizationAllowed === "true"}
                          onChange={(_, checked) =>
                            updateField("modernizationAllowed", checked ? "true" : "false")
                          }
                        />
                        {fieldErrors.modernizationAllowed && (
                          <div className={classes.fieldErrorText}>
                            {fieldErrors.modernizationAllowed}
                          </div>
                        )}
                      </div>
                      <div className={classes.fieldRowItem}>
                        <Toggle
                          label="Is this an incremental/renewal license? *"
                          onText="Yes"
                          offText="No"
                          checked={formValues.isIncremental === "true"}
                          onChange={(_, checked) =>
                            updateField("isIncremental", checked ? "true" : "false")
                          }
                        />
                        {fieldErrors.isIncremental && (
                          <div className={classes.fieldErrorText}>
                            {fieldErrors.isIncremental}
                          </div>
                        )}
                      </div>
                    </div>

                    {isIncremental && (
                      <div className={`${classes.fieldRow} ${classes.toggleRow}`}>
                        <div className={classes.fieldRowItem}>
                          <CloudFrameTextBox
                            label="Previous License ID"
                            required
                            className={classes.mb8}
                            value={formValues.previousLicenseId || ""}
                            onChange={(_, v) => updateField("previousLicenseId", v)}
                            errorMessage={fieldErrors.previousLicenseId}
                          />
                          {previousLicenseLookup.status === "loading" && (
                            <div className={classes.fieldErrorText}>
                              Checking previous license…
                            </div>
                          )}
                          {previousLicenseLookup.status === "not-found" && (
                            <div className={classes.fieldErrorText}>
                              Please enter a valid Previous License ID.
                            </div>
                          )}
                          {previousLicenseLookup.status === "not-activated" && (
                            <div className={classes.fieldErrorText}>
                              This license hasn&apos;t been activated on a machine yet. Please activate the previous license first.
                            </div>
                          )}
                          {previousLicenseLookup.status === "found" && (
                            <div
                              className={
                                previousLicenseLookup.carryOverLOC
                                  ? classes.fieldHintTextPositive
                                  : classes.fieldHintTextMuted
                              }
                            >
                              {previousLicenseLookup.carryOverLOC
                                ? "Carryover LOC is enabled — remaining lines of code will carry over from the previous license."
                                : "Carryover LOC is not added — the previous license has already expired."}
                            </div>
                          )}
                        </div>
                        <div className={classes.fieldRowItem}>
                          <Toggle
                            label="CarryOverLOC"
                            onText="Yes"
                            offText="No"
                            disabled
                            checked={
                              previousLicenseLookup.status === "found" &&
                              previousLicenseLookup.carryOverLOC
                            }
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {product === "Scanner" && (
              <div className={classes.fieldRow}>
                <div className={classes.fieldRowItem}>
                  <CloudFrameDatepicker
                    label="Scanner Effective Date *"
                    className={classes.mb8}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={defaultDatePickerStrings}
                    value={formValues.scannerEffectiveDate}
                    minDate={addDays(
                      getMinExpiryDate(),
                      -SCANNER_LICENSE_DURATION_DAYS
                    )}
                    onSelectDate={(d) => {
                      setFormValues((prev: any) => ({
                        ...prev,
                        scannerEffectiveDate: d ?? undefined,
                        scannerExpiryDate: d
                          ? addDays(d, SCANNER_LICENSE_DURATION_DAYS)
                          : undefined,
                      }));
                      setFieldErrors((prev) => {
                        if (!prev.scannerEffectiveDate && !prev.scannerExpiryDate) return prev;
                        const next = { ...prev };
                        delete next.scannerEffectiveDate;
                        delete next.scannerExpiryDate;
                        return next;
                      });
                    }}
                  />
                  {fieldErrors.scannerEffectiveDate && (
                    <div className={classes.fieldErrorText}>
                      {fieldErrors.scannerEffectiveDate}
                    </div>
                  )}
                </div>
                <div className={classes.fieldRowItem}>
                  <CloudFrameDatepicker
                    label="Scanner Expiry Date *"
                    className={classes.mb8}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={defaultDatePickerStrings}
                    value={formValues.scannerExpiryDate}
                    disabled
                  />
                  {fieldErrors.scannerExpiryDate && (
                    <div className={classes.fieldErrorText}>
                      {fieldErrors.scannerExpiryDate}
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && <div className={classes.errorBanner}>{error}</div>}

            <div className={classes.btnContainer}>
              <CloudFramePrimaryButton
                type="submit"
                text="Create License"
                disabled={submitting || isProductionBlocked || isCarryOverUnresolved}
              />
              <CloudFrameDefaultButton
                text="Cancel"
                onClick={() => navigate("/all-continuum-scanner-licenses")}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContinuumScannerLicenseForm;
